import { useEffect, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import type { Party, DossierListItem, DossierStatus } from "@/lib/types";
import { createDossier, updateDossier } from "./dossiers.api";

export const STATUS_CLASS: Record<DossierStatus, string> = {
  open: "wl-status--planned",
  in_progress: "wl-status--intransit",
  completed: "wl-status--delivered",
  archived: "wl-status--cancelled",
};

// Maps each status to its i18n key — use with t(STATUS_LABEL_KEYS[status]).
export const STATUS_LABEL_KEYS: Record<DossierStatus, string> = {
  open: "dossiers.statusOpen",
  in_progress: "dossiers.statusInProgress",
  completed: "dossiers.statusCompleted",
  archived: "dossiers.statusArchived",
};

export const STATUSES: DossierStatus[] = [
  "open",
  "in_progress",
  "completed",
  "archived",
];

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("nl-NL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const s = status as DossierStatus;
  const labelKey = STATUS_LABEL_KEYS[s];
  return (
    <span className={`wl-status ${STATUS_CLASS[s] ?? "wl-status--planned"}`}>
      <span className="wl-status__dot" />
      {labelKey ? t(labelKey as never) : status}
    </span>
  );
}

export interface DossierDrawerProps {
  item: DossierListItem | null; // null = create mode
  parties: Pick<Party, "id" | "name">[];
  onClose: () => void;
  onSave: (saved: DossierListItem) => void;
}

export function DossierDrawer({ item, parties, onClose, onSave }: DossierDrawerProps) {
  const { t } = useTranslation();
  const isCreate = item === null;
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const [partyId, setPartyId] = useState(item?.partyId ?? parties[0]?.id ?? "");
  const [primaryReference, setPrimaryReference] = useState(item?.primaryReference ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [status, setStatus] = useState<DossierStatus>(item?.status ?? "open");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    startTransition(async () => {
      try {
        let saved: DossierListItem;
        if (isCreate) {
          saved = await createDossier({
            partyId,
            primaryReference: primaryReference.trim(),
            description: description.trim() || undefined,
          });
        } else {
          saved = await updateDossier(item.id, {
            primaryReference: primaryReference.trim(),
            description: description.trim() || null,
            status,
          });
        }
        onSave(saved);
      } catch (e) {
        setError(e instanceof Error ? e.message : t("dossiers.errorGeneric"));
      }
    });
  }

  return (
    <div
      className="wl-drawer is-open"
      role="dialog"
      aria-label={isCreate ? t("dossiers.createAriaLabel") : `Edit: ${item.internalDossierNumber ?? item.primaryReference}`}
    >
      <div className="wl-drawer__header">
        <div className="wl-drawer__title" style={{ flex: 1 }}>
          {isCreate ? t("dossiers.newTitle") : (item.internalDossierNumber ?? "Dossier")}
        </div>
        <button
          className="wl-btn wl-btn--ghost wl-btn--icon"
          onClick={onClose}
          aria-label={t("dossiers.closeAriaLabel")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="wl-drawer__body">
        {error && (
          <div className="wl-alert wl-alert--danger" style={{ marginBottom: "16px" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {!isCreate && (
          <>
            <div className="wl-drawer__section">
              <div className="wl-drawer__section-title">{t("common.status")}</div>
              <StatusBadge status={item.status} />
            </div>
            <div className="wl-divider" style={{ marginBottom: "20px" }} />
          </>
        )}

        <form onSubmit={handleSubmit}>
          <div className="wl-drawer__section">
            <div className="wl-drawer__section-title">{t("common.details")}</div>

            <div className="wl-vstack" style={{ gap: "14px" }}>
              <div className="wl-field">
                <label className="wl-field__label" htmlFor="dos-party">
                  {t("dossiers.partyLabel")}
                </label>
                {isCreate ? (
                  <select
                    id="dos-party"
                    className="wl-select"
                    value={partyId}
                    onChange={(e) => setPartyId(e.target.value)}
                    required
                  >
                    {parties.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                ) : (
                  <div
                    className="t-body-sm"
                    style={{
                      padding: "0 10px",
                      height: "var(--control-height-md)",
                      display: "flex",
                      alignItems: "center",
                      background: "var(--surface-sunken)",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: "var(--radius-sm)",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {item.party.name}
                  </div>
                )}
              </div>

              <div className="wl-field">
                <label className="wl-field__label" htmlFor="dos-ref">
                  {t("dossiers.primaryRefLabel")}
                </label>
                <input
                  id="dos-ref"
                  className="wl-input"
                  type="text"
                  value={primaryReference}
                  onChange={(e) => setPrimaryReference(e.target.value)}
                  placeholder={t("dossiers.primaryRefPlaceholder")}
                  required
                  minLength={1}
                />
                <span className="wl-field__help">{t("dossiers.primaryRefHelp")}</span>
              </div>

              <div className="wl-field">
                <label className="wl-field__label" htmlFor="dos-desc">
                  {t("dossiers.descriptionLabel")}
                </label>
                <textarea
                  id="dos-desc"
                  className="wl-input"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("dossiers.descriptionPlaceholder")}
                  style={{ resize: "vertical", minHeight: "72px" }}
                />
              </div>

              {!isCreate && (
                <div className="wl-field">
                  <label className="wl-field__label" htmlFor="dos-status">
                    {t("dossiers.statusLabel")}
                  </label>
                  <select
                    id="dos-status"
                    className="wl-select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as DossierStatus)}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(STATUS_LABEL_KEYS[s] as never)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {!isCreate && (
            <>
              <div className="wl-divider" style={{ marginBottom: "20px" }} />
              <div className="wl-drawer__section">
                <div className="wl-drawer__section-title">{t("common.record")}</div>
                <div className="wl-vstack" style={{ gap: "8px" }}>
                  <div>
                    <div className="t-caption" style={{ marginBottom: "2px" }}>
                      {t("dossiers.dossierNumber")}
                    </div>
                    <div className="t-mono" style={{ fontSize: "12px" }}>{item.internalDossierNumber}</div>
                  </div>
                  <div>
                    <div className="t-caption" style={{ marginBottom: "2px" }}>
                      {t("common.created")}
                    </div>
                    <div className="t-body-sm">{formatDate(item.createdAt)}</div>
                  </div>
                  <div>
                    <div className="t-caption" style={{ marginBottom: "2px" }}>
                      {t("common.lastUpdated")}
                    </div>
                    <div className="t-body-sm">{formatDate(item.updatedAt)}</div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div style={{ marginTop: "24px", display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <button type="button" className="wl-btn wl-btn--secondary" onClick={onClose} disabled={isPending}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="wl-btn wl-btn--accent" disabled={isPending}>
              {isCreate ? t("dossiers.createButton") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
